import { useState } from 'react'
import { useTranslation } from 'react-i18next'

const LocationChangeModal = ({ 
  isOpen, 
  onClose, 
  oldLocation, 
  newLocation, 
  onConfirm, 
  onKeepCurrent 
}) => {
  const { t, i18n } = useTranslation()
  const [isLoading, setIsLoading] = useState(false)

  const handleConfirm = async () => {
    setIsLoading(true)
    try {
      await onConfirm()
    } catch (error) {
      console.error('Error updating location:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const handleKeepCurrent = () => {
    onKeepCurrent()
    onClose()
  }

  if (!isOpen) return null

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100vw",
        height: "100vh",
        background: "rgba(0, 0, 0, 0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 3000,
        fontFamily: '"Red Hat Display", sans-serif',
        padding: "20px",
        boxSizing: "border-box",
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose()
        }
      }}
      >
      <div
        style={{
          background: "#FFFFFF",
          borderRadius: "20px",
          padding: "40px",
          maxWidth: "650px",
          width: "100%",
          maxHeight: "90vh",
          overflow: "auto",
          boxShadow: "0 20px 25px rgba(0, 0, 0, 0.1)",
          position: "relative",
          textAlign: "center",
        }}
        onClick={(e) => e.stopPropagation()}
        dir={i18n.dir()}
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          style={{
            position: "absolute",
            top: "20px",
            right: "20px",
            background: "transparent",
            border: "none",
            cursor: "pointer",
            padding: "8px",
            borderRadius: "8px",
            transition: "background 0.2s ease",
          }}
          onMouseEnter={(e) => {
            e.target.style.background = "#F9FAFB"
          }}
          onMouseLeave={(e) => {
            e.target.style.background = "transparent"
          }}
          >
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#6B7280"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>

        {/* Modal Content */}
        <div style={{ marginBottom: "32px" }}>
          {/* Title */}
          <h2
            style={{
              fontFamily: '"Red Hat Display", sans-serif',
              fontSize: "24px",
              fontWeight: 600,
              color: "#303E4E",
              margin: "0 0 16px 0",
              lineHeight: "1.3",
            }}
          >
            {t("locationChangeModal.title")}
          </h2>

          {/* Description */}
          <p
            style={{
              fontFamily: '"Red Hat Display", sans-serif',
              fontSize: "16px",
              color: "#494949",
              margin: "0 0 32px 0",
              lineHeight: "1.6",
            }}
          >
            {t("locationChangeModal.description", {
              oldLocation: oldLocation || t("locationChangeModal.unknownLocation"),
              newLocation: newLocation || t("locationChangeModal.unknownLocation"),
            })}
          </p>

          {/* Action Buttons */}
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "16px",
              alignItems: "center",
            }}
          >
            {/* Update Community Button */}
            <button
              onClick={handleConfirm}
              disabled={isLoading}
              style={{
                width: "260px",
                height: "48px",
                background: isLoading ? "#9CA3AF" : "#8548A8",
                color: "#FFFFFF",
                border: "none",
                borderRadius: "12px",
                fontFamily: '"Red Hat Display", sans-serif',
                fontSize: "16px",
                fontWeight: 600,
                cursor: isLoading ? "not-allowed" : "pointer",
                transition: "all 0.2s ease",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "8px",
              }}
              onMouseEnter={(e) => {
                if (!isLoading) {
                  e.target.style.background = "#7A4397"
                  e.target.style.transform = "translateY(-1px)"
                }
              }}
              onMouseLeave={(e) => {
                if (!isLoading) {
                  e.target.style.background = "#8548A8"
                  e.target.style.transform = "translateY(0)"
                }
              }}
            >
              {isLoading ? t("locationChangeModal.buttons.updating") : t("locationChangeModal.buttons.update")}
            </button>

            {/* Keep Current Button */}
            <button
              onClick={handleKeepCurrent}
              disabled={isLoading}
              style={{
                width: "260px",
                height: "48px",
                background: "#FFFFFF",
                color: "#6B7280",
                border: "2px solid #E5E7EB",
                borderRadius: "12px",
                fontFamily: '"Red Hat Display", sans-serif',
                fontSize: "15px",
                fontWeight: 600,
                cursor: isLoading ? "not-allowed" : "pointer",
                transition: "all 0.2s ease",
                opacity: isLoading ? 0.6 : 1,
              }}
              onMouseEnter={(e) => {
                if (!isLoading) {
                  e.target.style.borderColor = "#D1D5DB"
                  e.target.style.color = "#374151"
                  e.target.style.transform = "translateY(-1px)"
                }
              }}
              onMouseLeave={(e) => {
                if (!isLoading) {
                  e.target.style.borderColor = "#E5E7EB"
                  e.target.style.color = "#6B7280"
                  e.target.style.transform = "translateY(0)"
                }
              }}
            >
              {t("locationChangeModal.buttons.keepCurrent")}
            </button>
          </div>
        </div>

        {/* Spinning animation */}
        <style>
          {`
            @keyframes spin {
              from {
                transform: rotate(0deg);
              }
              to {
                transform: rotate(360deg);
              }
            }
          `}
        </style>
      </div>
    </div>
  )
}

export default LocationChangeModal